import { useState } from 'react'
import { AlertCircle, CheckCircle2, Plus, RefreshCw, Webhook } from 'lucide-react'
import OrdersNavTabs from '@/components/orders/OrdersNavTabs'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import {
  useDeleteWebhookEndpoint,
  useRotateWebhookSecret,
  useTestWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useWebhookEndpoints,
} from '@/hooks/useOrderWebhooks'
import {
  getVerticalAppointmentWebhookCopy,
  type WebhookEndpoint,
} from '@/services/orderWebhooksApi'
import { useVerticalUI } from '@/lib/verticalUI'
import { WebhookEndpointCard } from './WebhookEndpointCard'
import { WebhookEndpointFormModal } from './WebhookEndpointFormModal'
import { WebhookSecretModal } from './WebhookSecretModal'
import { WebhookDeliveriesModal } from './WebhookDeliveriesModal'
import { WebhookHelpSection } from './WebhookHelpSection'

type FormState = { mode: 'create' } | { mode: 'edit'; endpoint: WebhookEndpoint } | null

interface SecretState {
  url: string
  secret: string
  mode: 'created' | 'rotated'
}

interface TestFeedback {
  endpointId: string
  ok: boolean
  message: string
}

export default function OrderWebhooksPage() {
  const { slug } = useVerticalUI()
  const { isAppointmentVertical, pageTitle, singularLabel, pluralLabel } =
    getVerticalAppointmentWebhookCopy(slug)
  const pageDescription = isAppointmentVertical
    ? `Cada vez que se solicita, confirma, reprograma o cancela una ${singularLabel.toLowerCase()} enviamos un aviso firmado a tu agenda, tu hoja de cálculo o una automatización.`
    : 'Cada vez que se crea o cambia un pedido enviamos un aviso firmado a tu ERP, tu hoja de cálculo o una automatización. Así tu inventario y tu contabilidad se mantienen al día sin copiar a mano.'
  const emptyDescription = isAppointmentVertical
    ? `Agrega la dirección de tu sistema y elige qué avisos de ${pluralLabel} quieres recibir. También puedes probarla antes de usarla con ${pluralLabel} reales.`
    : 'Agrega la dirección de tu sistema y elige qué avisos quieres recibir. También puedes probarla antes de usarla con pedidos reales.'

  const endpointsQuery = useWebhookEndpoints()
  const updateMutation = useUpdateWebhookEndpoint()
  const deleteMutation = useDeleteWebhookEndpoint()
  const rotateMutation = useRotateWebhookSecret()
  const testMutation = useTestWebhookEndpoint()

  const [form, setForm] = useState<FormState>(null)
  const [secretData, setSecretData] = useState<SecretState | null>(null)
  const [deliveriesEndpoint, setDeliveriesEndpoint] = useState<WebhookEndpoint | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpoint | null>(null)
  const [testFeedback, setTestFeedback] = useState<TestFeedback | null>(null)

  const endpoints = endpointsQuery.data ?? []

  async function handleTest(endpoint: WebhookEndpoint) {
    setTestFeedback(null)
    try {
      const result = await testMutation.mutateAsync(endpoint.id)
      setTestFeedback({
        endpointId: endpoint.id,
        ok: result.ok,
        message: result.ok
          ? `El destino respondió ${result.statusCode ?? 200} en ${result.durationMs} ms.`
          : result.error ?? 'El destino no respondió correctamente.',
      })
    } catch {
      // El hook ya muestra el mensaje de error.
    }
  }

  async function handleRotate(endpoint: WebhookEndpoint) {
    try {
      const result = await rotateMutation.mutateAsync(endpoint.id)
      setSecretData({ url: result.endpoint.url, secret: result.secret, mode: 'rotated' })
    } catch {
      // El hook ya muestra el mensaje de error.
    }
  }

  async function handleToggle(endpoint: WebhookEndpoint, enabled: boolean) {
    try {
      await updateMutation.mutateAsync({ id: endpoint.id, input: { enabled } })
    } catch {
      // El hook ya muestra el mensaje de error.
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    } catch {
      // El hook ya muestra el mensaje de error.
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <OrdersNavTabs />
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
        <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{pageTitle}</h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-600">{pageDescription}</p>
          </div>
          <Button type="button" onClick={() => setForm({ mode: 'create' })}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Agregar destino
          </Button>
        </header>

        {testFeedback && (
          <div
            role="status"
            className={`mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm ${
              testFeedback.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {testFeedback.ok ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            <span>{testFeedback.message}</span>
          </div>
        )}

        {endpointsQuery.isLoading && (
          <div className="space-y-3" aria-label="Cargando destinos">
            {[0, 1].map((row) => (
              <div key={row} className="h-40 animate-pulse rounded-xl bg-white" />
            ))}
          </div>
        )}

        {endpointsQuery.isError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-sm text-red-800">No se pudieron cargar los destinos.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => void endpointsQuery.refetch()}
            >
              <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Reintentar
            </Button>
          </div>
        )}

        {!endpointsQuery.isLoading && !endpointsQuery.isError && endpoints.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
            <Webhook className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
            <h2 className="mt-3 text-base font-semibold text-gray-900">Todavía no envías avisos</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">{emptyDescription}</p>
            <Button type="button" className="mt-4" onClick={() => setForm({ mode: 'create' })}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Agregar primer destino
            </Button>
          </div>
        )}

        {!endpointsQuery.isLoading && !endpointsQuery.isError && endpoints.length > 0 && (
          <div className="space-y-3">
            {endpoints.map((endpoint) => (
              <WebhookEndpointCard
                key={endpoint.id}
                endpoint={endpoint}
                testing={testMutation.isPending && testMutation.variables === endpoint.id}
                updating={updateMutation.isPending && updateMutation.variables?.id === endpoint.id}
                onTest={() => void handleTest(endpoint)}
                onHistory={() => setDeliveriesEndpoint(endpoint)}
                onRotate={() => void handleRotate(endpoint)}
                onEdit={() => setForm({ mode: 'edit', endpoint })}
                onDelete={() => setDeleteTarget(endpoint)}
                onToggle={(enabled) => void handleToggle(endpoint, enabled)}
              />
            ))}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">
          Cada aviso incluye una firma para que tu sistema compruebe que viene de nosotros. Los envíos fallidos
          se reintentan solos y, si un destino acumula 20 fallos seguidos, se desactiva para no saturar tu
          sistema.
        </p>

        <WebhookHelpSection />
      </div>

      {form && (
        <WebhookEndpointFormModal
          key={form.mode === 'edit' ? form.endpoint.id : 'create'}
          {...(form.mode === 'edit' ? { endpoint: form.endpoint } : {})}
          onClose={() => setForm(null)}
          onSaved={(result) => {
            setForm(null)
            if (result.secret) {
              setSecretData({ url: result.endpoint.url, secret: result.secret, mode: 'created' })
            }
          }}
        />
      )}

      {secretData && (
        <WebhookSecretModal
          url={secretData.url}
          secret={secretData.secret}
          mode={secretData.mode}
          onClose={() => setSecretData(null)}
        />
      )}

      {deliveriesEndpoint && (
        <WebhookDeliveriesModal
          endpoint={deliveriesEndpoint}
          onClose={() => setDeliveriesEndpoint(null)}
        />
      )}

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar destino"
        size="sm"
      >
        <p className="text-sm text-gray-700">
          Dejarás de enviar avisos a <span className="font-medium break-all">{deleteTarget?.url}</span> y se
          borrará su historial de entregas. Esta acción no se puede deshacer.
        </p>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => void confirmDelete()}
          >
            {deleteMutation.isPending ? 'Eliminando…' : 'Sí, eliminar destino'}
          </Button>
        </div>
      </Modal>
    </div>
  )
}
